import '../../assets/fonts.css';

import React, { useEffect, useRef } from 'react';
import { DefaultPromoWidget } from 'src/components/default-promo-widget';
import { LearnMore } from 'src/components/learn-more';
import { Portal } from 'src/components/portal';
import { ACTIONS, WIDGET_INITIALIZED } from 'src/constants/common';
import {
  AppProvider,
  type MerchantProps,
  useAppContext,
} from 'src/context/store/app-provider';
import { useAnalytics } from 'src/hooks/use-analytics';
import { useOnExperimentsReady } from 'src/hooks/use-on-experiments-ready';
import { usePromoSetup } from 'src/hooks/use-promo-setup';
import { mobileBridge } from 'src/services/mobile-bridge';
import { dataDog } from 'src/utils/datadog/data-dog';
import { delayDecorator } from 'src/utils/delay-decorator';
import { renderToByDefaultDecorator } from 'src/utils/render-to-by-default-decorator';
import { setupDatadogForWidgets } from 'src/utils/setup-datadog-for-widgets';

interface Props {
  selector?: string;
}

export const PromoWidget = ({ selector }) => {
  const { dispatch, learnMoreVisibility } = useAppContext();
  const logAnalyticEvent = useAnalytics();
  const ref = useRef(null);

  usePromoSetup(false, logAnalyticEvent, 'tabbyInfo');

  const shouldShowSchedule = learnMoreVisibility.pix;

  useEffect(() => {
    if (shouldShowSchedule) {
      dispatch({
        type: ACTIONS.PREPARE_SCHEDULE_POPUP,
      });
    }
  }, [shouldShowSchedule, dispatch]);

  useOnExperimentsReady(setupDatadogForWidgets);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      mobileBridge.sendDimensions({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Portal to={selector}>
        <div ref={ref}>
          <DefaultPromoWidget />
        </div>
      </Portal>

      <Portal to="body">
        <LearnMore />
      </Portal>
    </div>
  );
};

declare global {
  interface Window {
    TabbyPromo: (props?: Props & MerchantProps) => void;
  }
}

window.TabbyPromo = (props) => {
  dataDog.info('initialization', WIDGET_INITIALIZED, {
    constructorName: 'TabbyPromo',
    ...props,
  });

  delayDecorator(() => {
    renderToByDefaultDecorator(
      props?.selector || '',
      <AppProvider {...props}>
        <PromoWidget selector={props?.selector} />
      </AppProvider>
    );
  });
};
